import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import { formatRub } from "./PlatformDashboardParts";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

const MODULE_HELP = {
  analytics_pro: "Add-on: прогноз кассы, отток, воронка.",
  messengers: "Add-on: боты Telegram и Max.",
  bitrix24: "Add-on: клиенты, абонементы и занятия в Bitrix.",
  club_website: "Add-on: сайт-визитка, разовая покупка.",
  member_social:
    "Если фича уже в плане клуба — в кабинете «В плане», не покупка.",
  white_label:
    "Если брендинг уже в плане — в кабинете «В плане», не покупка.",
  trainer_portal:
    "Кабинет тренера. На «Клубе»+ — в плане; на «Старте» можно докупить.",
  client_portal:
    "Кабинет клиента. На «Клубе»+ — в плане; на «Старте» можно докупить.",
};

function emptyForm(mod) {
  return {
    name: mod?.name || "",
    description: mod?.description || "",
    price_rub: mod?.price_rub ?? 0,
    pricing: mod?.pricing || "monthly",
    is_enabled: Boolean(mod?.is_enabled),
    is_public: Boolean(mod?.is_public),
    allowed_subdomains: (mod?.allowed_subdomains || []).join(", "),
    sort_order: mod?.sort_order ?? 0,
  };
}

function accessLabel(mod) {
  if (!mod.is_enabled) return "Выключен";
  if (mod.is_public) return "Всем клубам";
  const list = (mod.allowed_subdomains || []).join(", ").trim();
  return list ? `Только: ${list}` : "Только выбранным (пусто)";
}

export default function PlatformModules() {
  const [modules, setModules] = useState([]);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState("");

  const load = () => {
    platformAPI
      .getModules()
      .then((res) => setModules(res.modules || []))
      .catch((e) => setErr(e.response?.data?.error || "Ошибка загрузки"));
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (mod) => {
    setEditing(mod);
    setForm(emptyForm(mod));
    setFormErr("");
  };

  const closeEdit = () => {
    setEditing(null);
    setFormErr("");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    setFormErr("");
    try {
      await platformAPI.updateModule(editing.code, {
        name: form.name,
        description: form.description,
        price_rub: Number(form.price_rub) || 0,
        pricing: form.pricing,
        is_enabled: form.is_enabled,
        is_public: form.is_public,
        allowed_subdomains: form.allowed_subdomains,
        sort_order: Number(form.sort_order) || 0,
      });
      closeEdit();
      load();
    } catch (e2) {
      setFormErr(e2.response?.data?.error || "Ошибка сохранения");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={s.page}>
      <PlatformPageHeader
        mobileOnlyBack
        backTo="/admin/more"
        backLabel="Ещё"
        title="Модули"
        lead="Add-ons поверх плана. «Выключен» у соцсети отключает стену у всех, даже если фича в тарифе."
      />

      <div className={s.billingHow}>
        <div className={s.billingHowItem}>
          <span className={s.billingHowNum}>1</span>
          <div>
            <strong>В плане</strong>
            <p>Фича в подписке и модуль не выключен — в кабинете «В плане», не покупка.</p>
          </div>
        </div>
        <div className={s.billingHowItem}>
          <span className={s.billingHowNum}>2</span>
          <div>
            <strong>Подключён</strong>
            <p>Купили или выдали вручную в карточке клуба.</p>
          </div>
        </div>
        <div className={s.billingHowItem}>
          <span className={s.billingHowNum}>3</span>
          <div>
            <strong>Доступен / Выключен</strong>
            <p>
              Открыт всем или списку — можно купить. Выключен — функция скрыта
              (у соцсети — без стены в профилях).
            </p>
          </div>
        </div>
      </div>

      {err ? <div className={s.alertError}>{err}</div> : null}

      <section className={s.moduleAdminGrid}>
        {modules.map((mod) => (
          <article
            key={mod.code}
            className={`${s.moduleAdminCard} ${
              mod.is_enabled ? "" : s.moduleAdminCardOff
            }`}
          >
            <div className={s.moduleAdminTop}>
              <div>
                <h3 className={s.moduleAdminName}>{mod.name}</h3>
                <p className={s.moduleAdminHelp}>
                  {MODULE_HELP[mod.code] ||
                    mod.description ||
                    "Дополнительный модуль"}
                </p>
              </div>
              <div className={s.moduleAdminPrice}>
                <strong>
                  {formatRub(mod.price_rub)} ₽
                  <small>
                    {mod.pricing === "once" ? " разово" : "/мес"}
                  </small>
                </strong>
              </div>
            </div>

            <div className={s.moduleAdminFlags}>
              <span
                className={`${s.statusPill} ${
                  mod.is_enabled ? s.statusActive : s.statusSuspended
                }`}
              >
                {mod.is_enabled ? "В каталоге" : "Скрыт"}
              </span>
              <span className={`${s.statusPill} ${s.statusNeutral}`}>
                {accessLabel(mod)}
              </span>
            </div>

            <button
              type="button"
              className={s.moduleAdminBtn}
              onClick={() => openEdit(mod)}
            >
              Настроить
            </button>
          </article>
        ))}
      </section>

      {editing ? (
        <div
          className={s.sheetOverlay}
          role="presentation"
          onClick={closeEdit}
        >
          <form
            className={s.sheetPanel}
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.sheetHead}>
              <h2 className={s.sheetTitle}>{editing.name}</h2>
              <button
                type="button"
                className={s.sheetClose}
                onClick={closeEdit}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className={s.sheetBody}>
              {formErr ? <div className={s.alertError}>{formErr}</div> : null}

              <div className={s.formGrid}>
                <label className={s.field}>
                  <span>Название</span>
                  <input
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                  />
                </label>
                <label className={s.field}>
                  <span>Цена, ₽</span>
                  <input
                    type="number"
                    min="0"
                    value={form.price_rub}
                    onChange={(e) =>
                      setForm({ ...form, price_rub: e.target.value })
                    }
                  />
                </label>
                <label className={s.field}>
                  <span>Как платят</span>
                  <select
                    value={form.pricing}
                    onChange={(e) =>
                      setForm({ ...form, pricing: e.target.value })
                    }
                  >
                    <option value="monthly">Каждый месяц</option>
                    <option value="once">Один раз</option>
                  </select>
                </label>
              </div>

              <label className={s.field}>
                <span>Короткое описание</span>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </label>

              <div className={s.moduleAccessRow}>
                <button
                  type="button"
                  className={`${s.moduleAccessBtn} ${
                    form.is_enabled && form.is_public ? s.moduleAccessBtnOn : ""
                  }`}
                  onClick={() =>
                    setForm({ ...form, is_enabled: true, is_public: true })
                  }
                >
                  Всем клубам
                </button>
                <button
                  type="button"
                  className={`${s.moduleAccessBtn} ${
                    form.is_enabled && !form.is_public
                      ? s.moduleAccessBtnOn
                      : ""
                  }`}
                  onClick={() =>
                    setForm({ ...form, is_enabled: true, is_public: false })
                  }
                >
                  Только списку
                </button>
                <button
                  type="button"
                  className={`${s.moduleAccessBtn} ${
                    !form.is_enabled ? s.moduleAccessBtnOn : ""
                  }`}
                  onClick={() => setForm({ ...form, is_enabled: false })}
                >
                  Выключить
                </button>
              </div>

              {form.is_enabled && !form.is_public ? (
                <label className={s.field}>
                  <span>Кому открыть (subdomain через запятую)</span>
                  <input
                    value={form.allowed_subdomains}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        allowed_subdomains: e.target.value,
                      })
                    }
                    placeholder="baysport, club1, club2"
                  />
                  <small className={s.fieldHint}>
                    Код клуба из ссылки: baysport.bay-sport.ru →{" "}
                    <code>baysport</code>
                  </small>
                </label>
              ) : null}

              <p className={s.fieldHint}>
                Выдать без оплаты — в{" "}
                <Link to="/admin/tenants">карточке клуба</Link>.
              </p>
            </div>

            <div className={s.sheetFooter}>
              <button
                type="button"
                className={s.heroActionBtnGhost}
                onClick={closeEdit}
              >
                Отмена
              </button>
              <button type="submit" className={s.heroActionBtn} disabled={busy}>
                {busy ? "Сохранение…" : "Сохранить"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
